'use client';

import React, { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { CaromaHubTradelinkProps } from './types';
import Typography from '@/components/atoms/typography';
import NewsImageSlider from '@/components/organisms/content/news-image-slider';

const CaromaHubTradelink = ({
  caromaHubTradelinkData,
}: CaromaHubTradelinkProps) => {
  const pathname = usePathname();
  const isAuYetiPromotion = pathname?.includes('au/yeti-promotion');

  const sections: any[] = caromaHubTradelinkData?.sections ?? [];

  // Sections are authored in Contentstack and can be reordered or omitted, so
  // resolve everything by block key rather than by position in the array.
  const cta = sections.find((section) => section?.cta)?.cta;

  const ctaTitle = isAuYetiPromotion ? cta?.link?.title : null;
  const ctaHref = isAuYetiPromotion ? cta?.link?.href : null;

  const [pageLoaded, setPageLoaded] = useState(false);

  useEffect(() => {
    const handleLoad = () => {
      setPageLoaded(true);
    };

    if (document.readyState === 'complete') {
      setPageLoaded(true);
    } else {
      window.addEventListener('load', handleLoad);
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, []);

  const renderBanner = (banner: any, offsetClass: string) => {
    const image = banner?.banner_image;

    if (!image?.url) return null;

    return (
      <div className={`w-full pb-[38px] ${offsetClass}`}>
        <img
          className="block h-auto w-full object-cover"
          src={image.url}
          alt={image?.description ?? image?.title ?? ''}
          loading="eager"
        />
      </div>
    );
  };

  const renderHeadingDescription = (block: any, offsetClass: string) => {
    const title = block?.title ?? '';
    const summary = block?.description ?? '';

    if (!title && !summary && !(ctaTitle && ctaHref)) return null;

    return (
      <div className={`flex ${offsetClass} justify-center termsOfUse`}>
        <div className="flex w-full flex-col items-center px-[36px] py-[18px] lg:px-[90px]">
          {title && (
            <Typography className="pb-3 text-center text-[24px] font-medium leading-10 text-gray-700 lg:text-[48px]">
              {title}
            </Typography>
          )}

          {summary && (
            <div
              className="w-full text-primary-black text-14 md:text-16 2xl:text-18 [&>h2]:text-[24px] [&>h2]:lg:text-[32px] [&>h2]:font-medium [&>p]:my-3 [&>br]:block text-justify pt-10"
              dangerouslySetInnerHTML={{ __html: summary }}
            />
          )}

          {isAuYetiPromotion && ctaTitle && ctaHref && (
            <div className="mt-6">
              <a
                href={ctaHref as string}
                className="inline-block rounded bg-black px-6 py-3 font-medium !text-white transition hover:bg-gray-800"
                style={{ color: '#ffffff' }}
              >
                {ctaTitle}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPromotionIcon = (block: any, offsetClass: string) => {
    const cards = block?.cards ?? [];

    if (cards.length === 0) return null;

    return (
      <div
        className={`grid grid-cols-1 gap-[30px] bg-[#F8F8F8] px-[36px] py-[18px] text-center md:gap-[20px] lg:grid-cols-3 lg:gap-[47px] lg:px-[90px] ${offsetClass}`}
      >
        {cards.map((card: any, index: number) => {
          const cardImage = card?.image;

          return (
            <div
              key={card?._metadata?.uid ?? index}
              className="flex flex-row lg:flex-col lg:items-center"
            >
              {/* Image */}
              {cardImage?.url && (
                <div className="flex h-[120px] w-[40%] items-center justify-center lg:h-[180px] lg:w-full">
                  <img
                    className="block h-auto max-h-full w-[120px] object-contain md:w-[180px]"
                    src={cardImage.url}
                    alt={cardImage?.description ?? ''}
                  />
                </div>
              )}

              {/* Desktop */}
              <div className="hidden lg:block lg:w-full">
                {card?.title && (
                  <Typography
                    as="h3"
                    className="min-h-[60px] text-20 font-bold leading-[1.5] text-primary-black xl:text-20 2xl:text-22"
                  >
                    {card.title}
                  </Typography>
                )}

                {card?.description && (
                  <div
                    className="text-16 font-normal leading-[1.5rem] text-primary-black md:text-14"
                    dangerouslySetInnerHTML={{
                      __html: card.description,
                    }}
                  />
                )}
              </div>

              {/* Mobile */}
              {card?.title && (
                <Typography
                  as="h3"
                  className="m-auto block w-[60%] text-20 font-bold leading-10 text-primary-black md:text-30 lg:hidden"
                >
                  {card.title}
                </Typography>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSection = (section: any, offsetClass: string) => {
    if (section?.section_with_banner) {
      return renderBanner(section.section_with_banner, offsetClass);
    }

    if (section?.section_with_heading_description) {
      return renderHeadingDescription(
        section.section_with_heading_description,
        offsetClass,
      );
    }

    if (section?.promotion_icon) {
      return renderPromotionIcon(section.promotion_icon, offsetClass);
    }

    if (section?.section_with_image_and_html) {
      // NewsImageSlider reads sections[0], so hand it this one section as a
      // single-element array rather than the whole entry.
      return <NewsImageSlider data={{ sections: [section] }} />;
    }

    return null;
  };

  return (
    <>
      {sections.map((section, index) => {
        const block = section?.[Object.keys(section ?? {})[0]];

        // Until the page has loaded the header is still out of flow, so the
        // topmost section carries the offset — whichever block type it is.
        const offsetClass = !pageLoaded && index === 0 ? 'mt-[181px]' : '';

        return (
          <React.Fragment key={block?._metadata?.uid ?? index}>
            {renderSection(section, offsetClass)}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default CaromaHubTradelink;
